import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { useAuthStore } from '@/stores/authStore'
import api from '@/services/api'

export default function AcceptInvitePage() {
  const navigate = useNavigate()
  const { setAuth } = useAuthStore()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token') || ''
  const email = searchParams.get('email') || ''

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')

  const acceptMutation = useMutation({
    mutationFn: (data: { token: string; email: string; password: string }) =>
      api.auth.acceptInvite(data),
    onSuccess: (response) => {
      const { user, token: authToken } = response.data.data
      const mappedUser = {
        id: user.userId,
        email: user.email,
        name: `${user.firstName} ${user.lastName}`.trim(),
        role: user.role,
        vendorId: user.tenantType === 'vendor' ? user.tenantId : undefined,
        subscriptionTier: user.subscriptionTier,
      }
      setAuth(mappedUser, authToken)

      if (mappedUser.vendorId) {
        navigate('/vendor')
      } else {
        navigate('/dashboard')
      }
    },
    onError: (err: any) => {
      setError(err.response?.data?.error?.message || err.response?.data?.message || 'Failed to accept invitation. The link may have expired.')
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    acceptMutation.mutate({ token, email, password })
  }

  if (!token || !email) {
    return (
      <div className="container" style={{ maxWidth: '450px', margin: '4rem auto', padding: '2rem' }}>
        <div className="card" style={{ textAlign: 'center' }}>
          <h1 style={{ marginBottom: '0.75rem' }}>Invalid Invitation Link</h1>
          <p style={{ color: 'var(--color-gray-600)', marginBottom: '1.5rem' }}>
            This invitation link is invalid or incomplete. Please check your email for the correct link, or ask your admin to resend the invitation.
          </p>
          <button onClick={() => navigate('/auth')} style={{ width: '100%' }}>
            Go to Sign In
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="container" style={{ maxWidth: '450px', margin: '4rem auto', padding: '2rem' }}>
      <div className="card">
        <h1 style={{ textAlign: 'center', marginBottom: '0.5rem' }}>
          Join Tixxly
        </h1>
        <p style={{ textAlign: 'center', color: 'var(--color-gray-600)', marginBottom: '0.25rem' }}>
          You've been invited to join as a team member.
        </p>
        <p style={{ textAlign: 'center', color: 'var(--color-gray-500)', marginBottom: '2rem', fontSize: '0.875rem' }}>
          {email}
        </p>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <div>
            <label htmlFor="password" style={{ display: 'block', marginBottom: '0.5rem' }}>
              Create Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              autoFocus
              style={{ width: '100%' }}
            />
          </div>

          <div>
            <label htmlFor="confirmPassword" style={{ display: 'block', marginBottom: '0.5rem' }}>
              Confirm Password
            </label>
            <input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
              style={{ width: '100%' }}
            />
          </div>

          <button
            type="submit"
            disabled={acceptMutation.isPending}
            style={{ width: '100%', marginTop: '0.5rem' }}
          >
            {acceptMutation.isPending ? 'Activating...' : 'Accept Invitation'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '1.5rem' }}>
          <button
            onClick={() => navigate('/auth')}
            style={{
              background: 'transparent',
              color: 'var(--color-gray-500)',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.875rem',
            }}
          >
            Already have an account? Sign In
          </button>
        </div>
      </div>
    </div>
  )
}
